import { groupBy, kebabCase } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'
import ReactMarkdown from 'react-markdown'

import { isNonNullable } from '@subaio/utils'

import { components } from 'components/DefaultMdxComponents'
import { getRefValue } from 'components/OpenApi/getRefValue'

import type { ItemProps } from './Item'

export const getApiEntries = (apiDocument: OpenApi3.Document, source: ItemProps['source']): Array<ItemProps> => {
	const entries = Object.entries(
		groupBy(Object.entries(apiDocument.paths!), ([, value]) => {
			if (!value) return
			const method = Object.values(value)[0]!
			if (typeof method !== 'string' && 'tags' in method) return method.tags
		}),
	)
		.flatMap(([title, paths]) => {
			const tag = apiDocument.tags?.find(({ name }) => name === title)
			const tagTitle = tag?.name ?? title
			const attributes: Array<Array<string>> = []

			const methods = paths
				.flatMap(([path]) => {
					if (!apiDocument.paths?.[path]) return null

					return Object.entries(apiDocument.paths[path]!)
						.map(([key, value]) => {
							if (typeof value !== 'string' && 'tags' in value) {
								if (value.responses) {
									Object.values(value.responses).forEach((response) => {
										if (
											'content' in response &&
											response.content?.['application/json']?.schema &&
											'$ref' in response.content['application/json'].schema
										) {
											const schema = getRefValue({
												...apiDocument.components,
												ref: response.content['application/json'].schema.$ref as string,
											})
											if (schema && 'properties' in schema && schema.properties) {
												attributes.push(Object.keys(schema.properties))
											}
										}
									})
								}

								if (
									value.requestBody &&
									'content' in value.requestBody &&
									value.requestBody.content['application/json']
								) {
									if (
										value.requestBody.content['application/json'].schema &&
										'$ref' in value.requestBody.content['application/json'].schema
									) {
										const schema = getRefValue({
											...apiDocument.components,
											ref: value.requestBody.content['application/json'].schema.$ref as string,
										})
										if (schema && 'properties' in schema && schema.properties) {
											attributes.push(Object.keys(schema.properties))
										}
									}
								}
								return {
									method: key,
									title: value.summary!,
									fragment: kebabCase(`${tagTitle}-${value.operationId!}`),
									description: value.description
										? ReactMarkdown({ components, children: value.description })
										: undefined,
									type: 'ApiEndpoint' as const,
									source,
									attributes: attributes.flat().map((attribute) => `.${attribute}`),
									url: path,
								}
							}
							return null
						})
						.filter(isNonNullable)
				})
				.filter(isNonNullable)

			return [
				{
					title: tag?.name ?? title,
					fragment: kebabCase(tagTitle),
					description: tag?.description ? ReactMarkdown({ components, children: tag.description }) : undefined,
					type: 'ApiTag' as const,
					source,
					paths: paths
						.map(([path]) => {
							if (!apiDocument.paths?.[path]) return null

							return {
								path,
								methods: Object.entries(apiDocument.paths[path]!)
									.map(([key, value]) =>
										typeof value !== 'string' && 'tags' in value
											? {
													method: key,
													title: value.summary!,
													fragment: kebabCase(`${tagTitle}-${value.operationId!}`),
													description: value.description,
												}
											: null,
									)
									.filter(isNonNullable),
							}
						})
						.filter(isNonNullable),
				},
				...methods,
			]
		})
		.filter(isNonNullable)

	return [
		{
			title: apiDocument.info.title!,
			description: apiDocument.info.description ? (
				<ReactMarkdown components={components}>{apiDocument.info.description}</ReactMarkdown>
			) : undefined,
			type: 'ApiDocument',
			source,
		},
		...entries,
	]
}
